import React, { useEffect, useState, useMemo } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Plus,
  Trash2,
  Layers,
  ChevronRight
} from "lucide-react";

import {
  getAllStandaloneSchemes,
  createStandaloneScheme,
  deleteStandaloneScheme
} from "../../api/markschedule/standaloneMarksApi";
import {
  getAllStandaloneSchemesPortal,
  createStandaloneSchemePortal,
  deleteStandaloneSchemePortal
} from "../../api/markschedule/standaloneMarksPortalApi";
import {
  getAllAcademicYears,
  getActiveAcademicYear
} from "../../api/academic/academicYearApi";
import {
  getAllAcademicYearsPortal,
  getActiveAcademicYearPortal
} from "../../api/academic/academicYearPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import {
  STANDALONE_SCHEME_TYPES,
  STANDALONE_SCHEME_TYPE_LABELS
} from "../../utils/markschedule/marksConstants";
import {
  pickList,
  toId
} from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const resolveYearName = (res) => {
  if (!res) return "";
  const y = res.academicYear || res.data || res;
  if (typeof y === "string") return y.trim();
  return (y.yearName || y.name || y.academicYear || y.label || "")
    .toString()
    .trim();
};

const StandaloneSchemeList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearReady, setYearReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterYear, setFilterYear] = useState("");
  const [years, setYears] = useState([]);
  const [form, setForm] = useState({
    name: "",
    type: "ASSIGNMENT",
    customTypeLabel: "",
    academicYear: "",
    maxMarks: "",
    description: "",
  });

  const toast = useToast();

  const base = isPortal
    ? "/staff-panel/marks/standalone"
    : "/marks/standalone";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allRes, activeRes] = await Promise.all([
          isPortal
            ? getAllAcademicYearsPortal({})
            : getAllAcademicYears({}),
          isPortal
            ? getActiveAcademicYearPortal()
            : getActiveAcademicYear(),
        ]);
        if (cancelled) return;
        const list = pickList(allRes, ["academicYears", "years", "data"]);
        const names = list
          .map((y) =>
            typeof y === "string"
              ? y
              : y.yearName || y.name || y.academicYear || ""
          )
          .filter(Boolean);
        const active = resolveYearName(activeRes);
        const yearSet = new Set(names);
        if (active) yearSet.add(active);
        const yearsArr = [...yearSet];
        setYears(yearsArr);
        setFilterYear(active || "");
        setForm((prev) => ({
          ...prev,
          academicYear: active || yearsArr[0] || prev.academicYear,
        }));
      } catch (e) {
        // non-fatal
      } finally {
        if (!cancelled) setYearReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const load = async () => {
    if (!yearReady) return;
    try {
      setLoading(true);
      const res = isPortal
        ? await getAllStandaloneSchemesPortal({
            academicYear: filterYear || undefined,
            isActive: "true",
          })
        : await getAllStandaloneSchemes({
            academicYear: filterYear || undefined,
            isActive: "true",
          });
      setSchemes(pickList(res, ["schemes", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load schemes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal, filterYear, yearReady]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.type || !form.academicYear || !form.maxMarks) {
      toast.error("Name, type, academic year and max marks are required");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        type: form.type,
        customTypeLabel: form.customTypeLabel || "",
        academicYear: form.academicYear.trim(),
        maxMarks: Number(form.maxMarks),
        description: form.description || "",
      };
      const res = isPortal
        ? await createStandaloneSchemePortal(payload)
        : await createStandaloneScheme(payload);
      const created = res.scheme || res.data || res;
      toast.success("Scheme created");
      if (created?._id) navigate(`${base}/${toId(created)}`);
      else load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this scheme?")) return;
    try {
      if (isPortal) await deleteStandaloneSchemePortal(id);
      else await deleteStandaloneScheme(id);
      toast.success("Scheme deactivated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  const yearOptions = useMemo(
    () => [
      { label: "All years", value: "" },
      ...years.map((y) => ({ label: y, value: y })),
    ],
    [years]
  );

  const formYearOptions = useMemo(
    () => [
      { label: "Academic year *", value: "" },
      ...years.map((y) => ({ label: y, value: y })),
    ],
    [years]
  );

  const typeOptions = useMemo(
    () =>
      STANDALONE_SCHEME_TYPES.map((t) => ({
        label: STANDALONE_SCHEME_TYPE_LABELS[t],
        value: t,
      })),
    []
  );

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          Standalone Marking Schemes
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Assignment / activity / club / game — no exam schedule required. Open a scheme to assign class/section/teacher and enter marks.
        </p>
      </div>

      {/* Filter Card */}
      <Card>
        <CardContent className="p-4">
          <div className="w-full sm:w-64">
            <Select
              label="Filter by Academic Year"
              options={yearOptions}
              value={filterYear}
              onChange={(val) => setFilterYear(val)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Alerts */}
      
      

      {/* Create Form */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary-900 dark:text-primary-300" /> Create Scheme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
                <Select
                  options={typeOptions}
                  value={form.type}
                  onChange={(val) => setForm({ ...form, type: val })}
                />
                <div>
                  <Select
                    options={formYearOptions}
                    value={form.academicYear}
                    onChange={(val) =>
                      setForm({ ...form, academicYear: val })
                    }
                    required
                  />
                  <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1">
                    Prefills active year
                  </p>
                </div>

                {form.type === "CUSTOM" && (
                  <Input
                    placeholder="Custom type label *"
                    value={form.customTypeLabel}
                    onChange={(e) =>
                      setForm({ ...form, customTypeLabel: e.target.value })
                    }
                  />
                )}
                <Input
                  type="number"
                  min={1}
                  placeholder="Max marks *"
                  value={form.maxMarks}
                  onChange={(e) =>
                    setForm({ ...form, maxMarks: e.target.value })
                  }
                  required
                />
                <Input
                  placeholder="Description"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={submitting}
                  disabled={submitting || !form.academicYear}
                >
                  Create Scheme
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Schemes List */}
      {loading || !yearReady ? (
        <div className="space-y-3">
          <Skeleton height={72} width="100%" />
          <Skeleton height={72} width="100%" />
          <Skeleton height={72} width="100%" />
        </div>
      ) : schemes.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={Layers}
              title="No schemes found"
              description="No standalone schemes exist for the selected academic year."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {schemes.map((s) => {
            const id = toId(s);
            return (
              <Card
                key={id}
                className="hover:border-neutral-300 transition-colors"
              >
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <button
                    type="button"
                    className="text-left group flex-1"
                    onClick={() => navigate(`${base}/${id}`)}
                  >
                    <div className="font-semibold text-primary-900 dark:text-primary-300 group-hover:underline inline-flex items-center gap-1.5 text-base">
                      {s.name}
                      <ChevronRight className="h-4 w-4 text-neutral-400 dark:text-neutral-500 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Badge variant="secondary" size="sm">
                        {STANDALONE_SCHEME_TYPE_LABELS[s.type] || s.type}
                        {s.customTypeLabel ? ` · ${s.customTypeLabel}` : ""}
                      </Badge>
                      <span>·</span>
                      <span>{s.academicYear}</span>
                      <span>·</span>
                      <span className="font-medium text-neutral-700 dark:text-neutral-200">
                        Max {s.maxMarks}
                      </span>
                    </div>
                  </button>

                  {canManage && (
                    <div className="shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-neutral-100 dark:border-neutral-800">
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => handleDelete(id)}
                        leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                        className="text-danger-600 hover:bg-danger-50"
                      >
                        Deactivate
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StandaloneSchemeList;